import { Box } from '@mui/material';
import { ReactNode } from 'react';
import {
    CONSOLE_CONTROL_GAP,
    consoleFieldFrameSx,
    consoleGroupSx,
} from '../chrome';
import { RowWindowingControls } from './tileLink';
import WindowLevelValues from './WindowLevelValues';
import WindowPresetMenu from './WindowPresetMenu';

interface ImageWindowControlsProps {
    /**
     * The control that names the image and changes which one it is. Every
     * image in a row is picked somehow, so the group takes the picker rather
     * than knowing which kind of image it holds.
     */
    readonly picker: ReactNode;
    /** Window/level reads and writes for the image the picker names. */
    readonly windowing: RowWindowingControls;
    /**
     * Whether the series' named windows, and the way back to its own
     * window among them, are offered beside its values. Off for a series
     * whose window is read in a unit of its own: none of its presets is
     * stated in that unit.
     */
    readonly withPresets?: boolean;
    /** Names the image, without PHI, e.g. its modality. */
    readonly imageLabel?: string;
}

/**
 * One image in a row, and how it is windowed: which image it is, then its
 * window in one frame with the list of its named windows joined to its
 * right edge.
 *
 * Every image in the row gets one of these, so no image is the lesser
 * control and a reader never has to work out which image a number belongs to.
 */
export default function ImageWindowControls({
    picker,
    windowing,
    withPresets = true,
    imageLabel,
}: ImageWindowControlsProps) {
    return (
        <Box sx={consoleGroupSx(CONSOLE_CONTROL_GAP)}>
            {picker}
            <Box sx={consoleGroupSx(0)}>
                <Box
                    data-testid="window-level-frame"
                    sx={consoleFieldFrameSx(withPresets)}
                >
                    <WindowLevelValues
                        source={windowing.source}
                        onSetWindowLevel={windowing.onSetWindowLevel}
                        onNudgeWindowLevel={windowing.onNudgeWindowLevel}
                    />
                </Box>
                {withPresets ? (
                    <WindowPresetMenu
                        source={windowing.source}
                        onSelectPreset={windowing.onSelectPreset}
                        onSetWindowLevel={windowing.onSetWindowLevel}
                        imageLabel={imageLabel}
                    />
                ) : null}
            </Box>
        </Box>
    );
}
